import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'

export interface UtmParams {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
}

export interface ShortLinkVideo {
  id: string
  title: string
  thumbnailUrl: string
  videoUrl: string
  durationLabel: string
  viewsLabel: string
  ratingLabel: string
  sortOrder: number
}

export interface ShortLink {
  id: string
  code: string
  targetUrl: string
  shortUrl: string
  mask: 'none' | 'twitter' | 'telegram' | 'instagram' | 'tiktok'
  ogTitle: string | null
  ogDescription: string | null
  ogImage: string | null
  utmParams: UtmParams | null
  clickCount: number
  isActive: boolean
  expiresAt: number | null
  createdAt: number
  domain: string | null
  lpEnabled: boolean
  lpRedirectMode: 'auto' | 'button'
  lpRedirectDelay: number
  lpSiteName: string | null
  lpSiteColor: string | null
  lpViewCount: number
  videos: ShortLinkVideo[]
}

export interface ShortLinkStats {
  totalLinks: number
  totalClicks: number
  activeLinks: number
}

export function useShortLinks() {
  return useQuery<ShortLink[]>({
    queryKey: ['short-links'],
    queryFn: () => api.get('/short-links'),
  })
}

export function useShortLinkStats() {
  return useQuery<ShortLinkStats>({
    queryKey: ['short-links', 'stats'],
    queryFn: () => api.get('/short-links/stats'),
  })
}

export function useCreateShortLink() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      targetUrl: string
      mask?: string
      ogTitle?: string
      ogDescription?: string
      ogImage?: string
      utmParams?: UtmParams
      domain?: string | null
      lpEnabled?: boolean
      lpRedirectMode?: 'auto' | 'button'
      lpRedirectDelay?: number
      lpSiteName?: string | null
      lpSiteColor?: string | null
    }) => api.post<ShortLink>('/short-links', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['short-links'] })
    },
  })
}

export function useUpdateShortLink() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: {
      id: string
      targetUrl?: string
      mask?: string
      ogTitle?: string | null
      ogDescription?: string | null
      ogImage?: string | null
      utmParams?: UtmParams | null
      isActive?: boolean
      domain?: string | null
      lpEnabled?: boolean
      lpRedirectMode?: 'auto' | 'button'
      lpRedirectDelay?: number
      lpSiteName?: string | null
      lpSiteColor?: string | null
    }) => api.put<ShortLink>(`/short-links/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['short-links'] })
    },
  })
}

export function useDeleteShortLink() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`/short-links/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['short-links'] })
    },
  })
}

// Video CRUD

export function useAddShortLinkVideo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ linkId, ...data }: {
      linkId: string
      mediaImageId?: string
      title: string
      thumbnailUrl: string
      videoUrl?: string
      durationLabel?: string
      viewsLabel?: string
      ratingLabel?: string
    }) => api.post<ShortLinkVideo>(`/short-links/${linkId}/videos`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['short-links'] })
    },
  })
}

export function useUpdateShortLinkVideo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ linkId, videoId, ...data }: {
      linkId: string
      videoId: string
      title?: string
      thumbnailUrl?: string
      videoUrl?: string
      durationLabel?: string
      viewsLabel?: string
      ratingLabel?: string
    }) => api.put<ShortLinkVideo>(`/short-links/${linkId}/videos/${videoId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['short-links'] })
    },
  })
}

export function useDeleteShortLinkVideo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ linkId, videoId }: { linkId: string; videoId: string }) =>
      api.delete<{ ok: boolean }>(`/short-links/${linkId}/videos/${videoId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['short-links'] })
    },
  })
}

export function useReorderShortLinkVideos() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ linkId, videoIds }: { linkId: string; videoIds: string[] }) =>
      api.post<{ ok: boolean }>(`/short-links/${linkId}/videos/reorder`, { videoIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['short-links'] })
    },
  })
}
